import React, { Dispatch, useContext, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useGetChildThreads } from '@juno/client-api';
import { CommunityUser, Thread } from '@juno/client-api/model';
import { ANALYTICS_CONFIGURATION } from '@juno/constants';
import { useSettings } from '@juno/utils';
import { useAnalyticsContext } from '../../Contexts/AnalyticsContext';
import { FeedContext } from '../../Feed';
import PostCard from '../PostCard';
import { ThreadPostSkeleton } from '../styles';
import CommentEditor from './CommentEditor';
import './styles.css';

interface CommentsRepliesSectionProps {
  thread: Thread;
  isInForum: boolean;
  handlePinUnpin: (thread: Thread) => Promise<void>;
  refetch: () => void;
  userCanInteract: boolean;
  setShowComments: Dispatch<React.SetStateAction<boolean>>;
}
const CommentsRepliesSectionInner: React.FC<CommentsRepliesSectionProps> = ({
  thread,
  isInForum,
  handlePinUnpin,
  refetch,
  userCanInteract,
  setShowComments,
}) => {
  const feedContext = useContext(FeedContext);
  const { user, site } = useSettings();
  const siteId = site?.id || '';

  const handleRefetch = () => {
    refetchChildren();
    refetch();
  };

  const editorRef = useRef<any>(null);

  const {
    data: threadChildren,
    isLoading: threadChildrenLoading,
    refetch: refetchChildren,
  } = useGetChildThreads(siteId, thread.id, {}, { query: { enabled: !!thread.id } });

  const userMentions = useRef<CommunityUser[]>(thread.tagged_users ?? []);
  const { firehoseActivity } = useAnalyticsContext();
  const topRef = useRef<HTMLDivElement>(null);

  if (!thread || !user) return null;

  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0.3 }} transition={{ duration: 0.4 }}>
      <Typography variant='h5' mb='20px'>
        Comments ({threadChildren?.length || 0})
      </Typography>
      <Divider />

      {threadChildrenLoading && <ThreadPostSkeleton />}
      {!threadChildrenLoading && (!threadChildren || threadChildren?.length === 0) && (
        <>
          <Typography my='20px' color='primary' textAlign='center'>
            No comments yet
          </Typography>
          <Divider />
        </>
      )}
      {!threadChildrenLoading && threadChildren && threadChildren.length > 0 && (
        <Box
          sx={{
            overflowY: 'auto',
          }}
        >
          <Box ref={topRef}></Box>
          {threadChildren
            .sort((a: Thread, b: Thread) => {
              return (
                new Date(b.date_created ?? '').getTime() - new Date(a.date_created ?? '').getTime()
              );
            })
            .map((thread: Thread) => (
              <PostCard
                userCanInteract={userCanInteract}
                key={`comment-${thread.id}`}
                isInForum={isInForum}
                thread={thread}
                refetch={handleRefetch}
                handleSelect={(newThread) => {
                  feedContext?.setThreadToReplyTo(newThread);
                }}
                handlePinUnpin={() => handlePinUnpin(thread)}
              />
            ))}
        </Box>
      )}

      <Box p={2}>
        <CommentEditor
          showAvatar
          editorRef={editorRef}
          userMentions={userMentions}
          onCancel={() => {
            feedContext?.setThreadToReplyTo(null);
            editorRef.current?.setContent('');
            setShowComments(false);
          }}
          parentThreadId={thread.id}
          onSubmitSuccess={() => {
            handleRefetch();
            editorRef.current?.setContent('');
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
            firehoseActivity(
              ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_COMMUNITY,
              thread?.parent_id || '',
              null,
              `${user?.id || ''}`,
              isInForum
                ? ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FORUM
                : ANALYTICS_CONFIGURATION.FIREHOSE_CATEGORIES.CATEGORY_FEED,
              feedContext?.threadToReplyTo?.id === thread.id
                ? ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_COMMENT.value
                : ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_REPLY.value,
              '',
              null,
            );
          }}
        />
      </Box>
    </motion.div>
  );
};

const CommentsRepliesSection: React.FC<CommentsRepliesSectionProps> = ({
  thread,
  isInForum,
  handlePinUnpin,
  refetch,
  userCanInteract,
  setShowComments,
}) => {
  return (
    <CommentsRepliesSectionInner
      thread={thread}
      isInForum={isInForum}
      handlePinUnpin={handlePinUnpin}
      refetch={refetch}
      userCanInteract={userCanInteract}
      setShowComments={setShowComments}
    />
  );
};

export default CommentsRepliesSection;
